import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  RefreshCw, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Database, Filter, Columns3, Download
} from 'lucide-react';
import GlobalSearch from './globalfunctions/GlobalSearch';
import { useDataLogsStore } from '../store/dataLogsStore';
import { settingsColorPaletteService, ColorPalette } from '../services/settingsColorPaletteService';
import { DataLogRecord } from '../services/dataLogsService';
import { exportToCSV } from '../utils/exportUtils';

const hexToRgba = (hex: string, opacity: number) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${opacity})` : hex;
};

const allColumns = [
  { key: 'log_type', label: 'Log Type', width: 'min-w-44' },
  { key: 'id', label: 'ID', width: 'min-w-16' },
  { key: 'old_details', label: 'Old Details', width: 'min-w-[340px]' },
  { key: 'new_details', label: 'New Details', width: 'min-w-[340px]' },
  { key: 'created_at', label: 'Created At', width: 'min-w-44' },
  { key: 'created_by', label: 'Created By', width: 'min-w-48' },
  { key: 'updated_at', label: 'Updated At', width: 'min-w-44' },
  { key: 'updated_by', label: 'Updated By', width: 'min-w-48' },
];

const DataLogs: React.FC = () => {
  const { logRecords, isLoading, error, fetchLogRecords, refreshLogRecords } = useDataLogsStore();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [logTypeFilter, setLogTypeFilter] = useState<string>('all');
  const [colorPalette, setColorPalette] = useState<ColorPalette | null>(null);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => document.documentElement.classList.contains('dark'));

  // Columns toggle
  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState<boolean>(false);
  const columnsDropdownRef = useRef<HTMLDivElement>(null);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem('dataLogsVisibleColumns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (err) {
        console.error('Failed to load visible columns:', err);
      }
    }
    return allColumns.map(col => col.key);
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(25);

  useEffect(() => {
    const fetchColorPalette = async () => {
      try {
        const activePalette = await settingsColorPaletteService.getActive();
        setColorPalette(activePalette);
      } catch (err) {
        console.error('Failed to fetch color palette:', err);
      }
    };
    fetchColorPalette();
  }, []);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Dropdown dismiss on outside clicks
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (columnsDropdownRef.current && !columnsDropdownRef.current.contains(target)) {
        setColumnsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load records on mount
  useEffect(() => {
    fetchLogRecords();
  }, [fetchLogRecords]);

  // Reset pagination when filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, logTypeFilter]);

  const userOrgId = useMemo(() => {
    try {
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      return authData.organization_id || authData.user?.organization_id || authData.organization?.id || authData.user?.organization?.id || null;
    } catch {
      return null;
    }
  }, []);

  // Client-side instant live filtering
  const filteredLogs = useMemo(() => {
    return logRecords.filter((row) => {
      // Organization filter — mirrors applicationmanagement.tsx logic exactly
      if (userOrgId) {
        if ((row as any).organization_id !== userOrgId) return false;
      } else {
        if ((row as any).organization_id) return false;
      }
      // Filter by log type
      if (logTypeFilter !== 'all') {
        const rowType = row.log_type.toLowerCase();
        const filter = logTypeFilter.toLowerCase();

        if (filter === 'service_order' && !rowType.includes('service order') && !rowType.includes('serviceorders') && !rowType.includes('serviceorder')) return false;
        if (filter === 'job_order' && !rowType.includes('job order') && !rowType.includes('joborders') && !rowType.includes('joborder')) return false;
        if (filter === 'application' && !rowType.includes('application')) return false;
        if (filter === 'customer_details' && !rowType.includes('customer details')) return false;
        if (filter === 'billing_details' && !rowType.includes('billing details')) return false;
        if (filter === 'technical_details' && !rowType.includes('technical details')) return false;
        if (filter === 'other' && (
          rowType.includes('service order') || rowType.includes('serviceorders') || rowType.includes('serviceorder') ||
          rowType.includes('job order') || rowType.includes('joborders') || rowType.includes('joborder') ||
          rowType.includes('application') ||
          rowType.includes('customer details') ||
          rowType.includes('billing details') ||
          rowType.includes('technical details')
        )) return false;
      }

      // Filter by search query
      if (searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase();
        const matchType = row.log_type.toLowerCase().includes(q);
        const matchId = row.id.toLowerCase().includes(q);
        const matchCreatedBy = row.created_by.toLowerCase().includes(q);
        const matchUpdatedBy = row.updated_by.toLowerCase().includes(q);
        const matchOld = row.old_details ? row.old_details.toLowerCase().includes(q) : false;
        const matchNew = row.new_details ? row.new_details.toLowerCase().includes(q) : false;
        return matchType || matchId || matchCreatedBy || matchUpdatedBy || matchOld || matchNew;
      }

      return true;
    });
  }, [logRecords, logTypeFilter, searchQuery, userOrgId]);



  const handleToggleColumn = (columnKey: string) => {
    setVisibleColumns(prev => {
      const next = prev.includes(columnKey)
        ? prev.filter(key => key !== columnKey)
        : [...prev, columnKey];
      localStorage.setItem('dataLogsVisibleColumns', JSON.stringify(next));
      return next;
    });
  };

  const handleSelectAllColumns = () => {
    const allKeys = allColumns.map(col => col.key);
    setVisibleColumns(allKeys);
    localStorage.setItem('dataLogsVisibleColumns', JSON.stringify(allKeys));
  };

  const handleDeselectAllColumns = () => {
    setVisibleColumns([]);
    localStorage.setItem('dataLogsVisibleColumns', JSON.stringify([]));
  };

  const handleExport = () => {
    if (!filteredLogs || filteredLogs.length === 0) return;

    const exportColumns = allColumns.filter(col => visibleColumns.includes(col.key));
    const getExportValue = (record: DataLogRecord, columnKey: string) => {
      switch (columnKey) {
        case 'old_details':
          return record.old_details ? JSON.stringify(record.old_details) : '';
        case 'new_details':
          return record.new_details ? JSON.stringify(record.new_details) : '';
        default:
          return (record as any)[columnKey] || '';
      }
    };

    exportToCSV('data_logs_export', exportColumns, filteredLogs, getExportValue);
  };

  const renderDetailsJson = (val: any, row: DataLogRecord, columnKey: 'old_details' | 'new_details') => {
    if (!val) return '-';

    try {
      const parseData = (v: any) => {
        if (!v) return {};
        const parsed = typeof v === 'string' ? JSON.parse(v) : v;
        return parsed.data || parsed;
      };

      const data = parseData(val);
      const otherKey = columnKey === 'old_details' ? 'new_details' : 'old_details';
      const otherData = parseData(row[otherKey]);

      const oldData = parseData(row.old_details);
      const newData = parseData(row.new_details);

      const allKeys = Array.from(new Set([...Object.keys(oldData), ...Object.keys(newData)]))
        .filter(k => k !== 'type')
        .sort();

      if (allKeys.length === 0) return '-';

      const formatValue = (v: any) => {
        if (v === null || v === undefined || v === '') return '(empty)';
        if (typeof v === 'object') {
          try {
            return JSON.stringify(v).replace(/[{}"]/g, '').replace(/:/g, ': ').replace(/,/g, ', ');
          } catch (e) {
            return '[Complex Object]';
          }
        }
        return String(v);
      };

      return (
        <ul className="text-xs w-full break-words max-h-[180px] overflow-y-auto overflow-x-hidden p-1 custom-scroll space-y-0.5">
          {allKeys.map((k) => {
            const v = data[k];
            const v_other = otherData[k];
            const displayKey = k.replace(/_/g, ' ')
              .split(' ')
              .map(w => w.charAt(0).toUpperCase() + w.slice(1))
              .join(' ');

            return (
              <li key={k} className="mb-0.5 grid grid-cols-1 grid-rows-1">
                <div className="invisible row-start-1 col-start-1 pointer-events-none" aria-hidden="true">
                  <span className="font-semibold">{displayKey}:</span>{' '}
                  <span className="break-all">{formatValue(v)}</span>
                </div>
                <div className="invisible row-start-1 col-start-1 pointer-events-none" aria-hidden="true">
                  <span className="font-semibold">{displayKey}:</span>{' '}
                  <span className="break-all">{formatValue(v_other)}</span>
                </div>

                <div className="row-start-1 col-start-1">
                  <span className={`font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>{displayKey}:</span>{' '}
                  <span className={`opacity-95 break-all ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{formatValue(v)}</span>
                </div>
              </li>
            );
          })}
        </ul>
      );
    } catch (e) {
      return <span className={`text-xs break-all ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{String(val)}</span>;
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredLogs.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredLogs, currentPage]);

  const activeColor = colorPalette?.primary || '#6366f1';

  return (
    <div className={`h-full flex flex-col overflow-hidden ${isDarkMode ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-900'}`}>

      {/* Main Glassmorphism container */}
      <div className="w-full flex-1 flex flex-col overflow-hidden">

        {/* Header Block matching PaymentPortal.tsx */}
        <div className={`px-6 py-4 border-b flex items-center space-x-3 ${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-300'
          }`}>
          {/* Title Label */}
          <div className="flex items-center mr-2 whitespace-nowrap flex-shrink-0">
            <span className={`text-sm font-bold uppercase tracking-wider ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Data Logs
            </span>
          </div>

          {/* Expanded Search Input */}
          <GlobalSearch
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            isDarkMode={isDarkMode}
            colorPalette={colorPalette}
            placeholder="Search data logs..."
          />

          {/* Column Visibility Dropdown */}
          <div className="relative" ref={columnsDropdownRef}>
            <button
              onClick={() => setColumnsDropdownOpen(!columnsDropdownOpen)}
              title="Column Visibility"
              className={`px-4 py-2 rounded text-sm transition-colors flex items-center ${isDarkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-200 text-gray-900 border border-gray-300'
                }`}
            >
              <Columns3 className="h-5 w-5" />
            </button>
            {columnsDropdownOpen && (
              <div className={`absolute top-full right-0 mt-2 w-80 rounded shadow-lg z-50 max-h-[70vh] flex flex-col ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                }`}>
                <div className={`p-3 border-b flex items-center justify-between ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Column Visibility</span>
                  <div className="flex space-x-2">
                    <button onClick={handleSelectAllColumns} className="text-xs" style={{ color: activeColor }}>
                      Select All
                    </button>
                    <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>|</span>
                    <button onClick={handleDeselectAllColumns} className="text-xs" style={{ color: activeColor }}>
                      Deselect All
                    </button>
                  </div>
                </div>
                <div className="overflow-y-auto flex-1 p-2 space-y-1">
                  {allColumns.map((column) => (
                    <label
                      key={column.key}
                      className={`flex items-center px-3 py-1.5 cursor-pointer text-xs rounded transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-100 text-gray-900'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(column.key)}
                        onChange={() => handleToggleColumn(column.key)}
                        className={`mr-3 h-4 w-4 rounded ${isDarkMode ? 'border-gray-600 bg-gray-700 focus:ring-offset-gray-800' : 'border-gray-300 bg-white focus:ring-offset-white'
                          }`}
                        style={{ accentColor: activeColor }}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Log Type select filter */}
          <select
            value={logTypeFilter}
            onChange={(e) => setLogTypeFilter(e.target.value)}
            className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-all cursor-pointer focus:outline-none ${logTypeFilter !== 'all'
                ? 'text-red-500 border-red-500/50 focus:border-red-500'
                : isDarkMode
                  ? 'bg-gray-900 border-gray-800 text-white hover:bg-gray-800'
                  : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-100'
              }`}
            style={{
              height: '38px'
            }}
          >
            <option value="all" className={isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}>All Log Types</option>
            <option value="service_order" className={isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}>Service Orders</option>
            <option value="job_order" className={isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}>JobOrders</option>
            <option value="application" className={isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}>Applications</option>
            <option value="customer_details" className={isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}>Customer Details</option>
            <option value="billing_details" className={isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}>Billing Details</option>
            <option value="technical_details" className={isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}>Technical Details</option>
            <option value="other" className={isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}>Other System Logs</option>
          </select>

          {/* Export to CSV Button */}
          <button
            onClick={handleExport}
            disabled={isLoading || filteredLogs.length === 0}
            title="Export to CSV"
            className="p-2 rounded-lg transition-all duration-200 flex items-center justify-center border disabled:opacity-50"
            style={{
              backgroundColor: isDarkMode ? '#111827' : '#ffffff',
              borderColor: activeColor,
              color: activeColor
            }}
            onMouseEnter={(e) => {
              if (!isLoading && filteredLogs.length > 0) {
                e.currentTarget.style.backgroundColor = hexToRgba(activeColor, 0.1);
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading && filteredLogs.length > 0) {
                e.currentTarget.style.backgroundColor = isDarkMode ? '#111827' : '#ffffff';
              }
            }}
          >
            <Download className="h-5 w-5" />
          </button>

          {/* Refresh Button */}
          <button
            onClick={() => refreshLogRecords()}
            disabled={isLoading}
            title="Refresh Logs"
            className="p-2 rounded-lg transition-all duration-200 flex items-center justify-center border disabled:opacity-50"
            style={{
              backgroundColor: isDarkMode ? '#111827' : '#ffffff',
              borderColor: activeColor,
              color: activeColor
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = hexToRgba(activeColor, 0.1);
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = isDarkMode ? '#111827' : '#ffffff';
              }
            }}
          >
            <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Content/Table Block */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="py-24 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 mx-auto mb-4" style={{ borderColor: activeColor }}></div>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading consolidated logs...</p>
            </div>
          ) : error ? (
            <div className="py-24 text-center">
              <p className="text-red-500 font-bold mb-4">{error}</p>
              <button
                onClick={() => refreshLogRecords()}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl"
              >
                Retry
              </button>
            </div>
          ) : paginatedLogs.length > 0 ? (
            <table className="w-full min-w-max text-left border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className={`${isDarkMode ? 'bg-gray-900 border-gray-800' : 'bg-gray-100 border-gray-300'} border-b`}>
                  {allColumns
                    .filter(col => visibleColumns.includes(col.key))
                    .map(col => (
                      <th
                        key={col.key}
                        className={`py-3 px-6 text-xs font-bold uppercase tracking-wider ${col.width || ''} ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                      >
                        {col.label}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {paginatedLogs.map((row) => (
                  <tr
                    key={`${row.log_type}-${row.id}`}
                    className={`border-b transition-colors hover:bg-slate-500/5 ${isDarkMode ? 'border-gray-800/80 hover:bg-gray-800/30' : 'border-gray-200 hover:bg-gray-100/30'
                      }`}
                  >
                    {visibleColumns.includes('log_type') && (
                      <td className="py-4 px-6 align-top whitespace-nowrap">
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {row.log_type}
                        </span>
                      </td>
                    )}
                    {visibleColumns.includes('id') && (
                      <td className={`py-4 px-6 align-top text-xs font-bold ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {row.id}
                      </td>
                    )}
                    {visibleColumns.includes('old_details') && (
                      <td className="py-4 px-6 align-top max-w-[450px]">
                        {renderDetailsJson(row.old_details, row, 'old_details')}
                      </td>
                    )}
                    {visibleColumns.includes('new_details') && (
                      <td className="py-4 px-6 align-top max-w-[450px]">
                        {renderDetailsJson(row.new_details, row, 'new_details')}
                      </td>
                    )}
                    {visibleColumns.includes('created_at') && (
                      <td className={`py-4 px-6 align-top whitespace-nowrap text-xs ${isDarkMode ? 'text-slate-200' : 'text-gray-900'}`}>
                        {row.created_at || '-'}
                      </td>
                    )}
                    {visibleColumns.includes('created_by') && (
                      <td className={`py-4 px-6 align-top text-xs max-w-[180px] truncate ${isDarkMode ? 'text-slate-200' : 'text-gray-900'}`} title={row.created_by}>
                        {row.created_by}
                      </td>
                    )}
                    {visibleColumns.includes('updated_at') && (
                      <td className={`py-4 px-6 align-top whitespace-nowrap text-xs ${isDarkMode ? 'text-slate-200' : 'text-gray-900'}`}>
                        {row.updated_at || '-'}
                      </td>
                    )}
                    {visibleColumns.includes('updated_by') && (
                      <td className={`py-4 px-6 align-top text-xs max-w-[180px] truncate ${isDarkMode ? 'text-slate-200' : 'text-gray-900'}`} title={row.updated_by}>
                        {row.updated_by}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="py-24 text-center">
              <p className={`text-sm ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>No data logs found matching your filters</p>
            </div>
          )}
        </div>

        {/* Pagination Footer */}
        {!isLoading && !error && filteredLogs.length > 0 && (
          <div className={`border-t p-4 flex items-center justify-between ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`flex items-center gap-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <div className="flex items-center gap-2">
                <span>Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={`px-2 py-1 rounded border focus:outline-none text-xs transition-colors ${isDarkMode
                      ? 'bg-gray-800 border-gray-700 text-white focus:border-orange-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-orange-500'
                    }`}
                >
                  {[10, 25, 50, 100].map(v => (
                    <option key={v} value={v} className={isDarkMode ? 'bg-gray-850 text-white' : 'bg-white text-gray-900'}>{v}</option>
                  ))}
                </select>
                <span>entries</span>
              </div>
              <div>
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredLogs.length)}</span> of <span className="font-medium">{filteredLogs.length}</span> results
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`px-2 py-1 rounded text-sm transition-colors ${currentPage === 1
                  ? (isDarkMode ? 'text-gray-600 bg-gray-800 cursor-not-allowed' : 'text-gray-400 bg-gray-100 cursor-not-allowed')
                  : (isDarkMode ? 'text-white bg-gray-700 hover:bg-gray-600' : 'text-gray-700 bg-white hover:bg-gray-50 border border-gray-300')
                  }`}
                title="First Page"
              >
                <ChevronsLeft size={16} />
              </button>

              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded text-sm transition-colors ${currentPage === 1
                  ? (isDarkMode ? 'text-gray-600 bg-gray-800 cursor-not-allowed' : 'text-gray-400 bg-gray-100 cursor-not-allowed')
                  : (isDarkMode ? 'text-white bg-gray-700 hover:bg-gray-600' : 'text-gray-700 bg-white hover:bg-gray-50 border border-gray-300')
                  }`}
                title="Previous Page"
              >
                <ChevronLeft size={16} />
              </button>

              <div className="flex items-center space-x-1">
                <span className={`px-2 text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Page {currentPage} of {totalPages}
                </span>
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded text-sm transition-colors ${currentPage === totalPages
                  ? (isDarkMode ? 'text-gray-600 bg-gray-800 cursor-not-allowed' : 'text-gray-400 bg-gray-100 cursor-not-allowed')
                  : (isDarkMode ? 'text-white bg-gray-700 hover:bg-gray-600' : 'text-gray-700 bg-white hover:bg-gray-50 border border-gray-300')
                  }`}
                title="Next Page"
              >
                <ChevronRight size={16} />
              </button>

              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-2 py-1 rounded text-sm transition-colors ${currentPage === totalPages
                  ? (isDarkMode ? 'text-gray-600 bg-gray-800 cursor-not-allowed' : 'text-gray-400 bg-gray-100 cursor-not-allowed')
                  : (isDarkMode ? 'text-white bg-gray-700 hover:bg-gray-600' : 'text-gray-700 bg-white hover:bg-gray-50 border border-gray-300')
                  }`}
                title="Last Page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataLogs;
